/* eslint no-console: 0 */
/* eslint no-warning-comments: 0 */
import _ from 'lodash';
import {formatNameForUrl, formatTitle, sanitizeText} from './formatHelpers';
import {
  getAnalyticPageCategory,
  isFuneralHomePageType,
} from '../../reactModules/shared/utilities/personPageType';
import {buildUrl} from '../../reactModules/shared/utilities/urlHelpers';
import appConfig from '../../appConfig/AppConfig';
import {
  PERSON_PAGE_TYPE,
  GA_SITENAMES,
} from '../../reactModules/shared/utilities/constants';
import {
  addNewsArticleSchema,
  addPersonEventsSchema,
  addPersonSchema,
  addItemListSchema,
  addFuneralHomeAndEventSchema,
} from '../services/jsonld/JsonLd';

const conf = appConfig();

/**
 * Gets the ref key for the global ad variables.
 * Originally implemented as part of EL-749 (https://legacycom.atlassian.net/browse/EL-749)
 * The majority of the logic is copied from getReferrerDomain method of the Legacy_Omaha repo.
 * https://bitbucket.org/legacycom/legacy_omaha/src/540956c5670cc9f7d694f95af314123c6c0203fb/Legacy.Omaha.WebUI/AdControls/OmahaAdControlDfp.cs#lines-171:187
 *
 * @param {string} referer request header referer
 * @return {string} ref string
 */
export function getRefValue(referer = '') {
  if (!referer) {
    return 'none';
  }

  if (referer.includes('legacy.com')) {
    if (
      referer.includes('/search') ||
      referer.includes('obituary-search.aspx')
    ) {
      return 'Legacy-Search';
    }

    if (referer.includes('/funeral-homes/')) {
      return 'Legacy-FHDirectory';
    }

    return 'Legacy';
  }

  if (referer.includes('google.com')) {
    return 'google';
  }

  return 'NonLegacy';
}

/**
 * Constructs the meta description for profile page
 * @method getProfileDescription
 * @param  {object} funeralHome   the funeral home object
 * @return {string} returns the fully formed meta description
 */
export function getProfileDescription(funeralHome) {
  return `View upcoming funeral services, obituaries, and funeral flowers for ${
    funeralHome.name
  } in ${funeralHome.location.city.fullName}, ${
    funeralHome.location.state.code
  }. Find contact information, view maps, and more.`;
}

/**
 * Constructs the og:title for the profile open graph usage
 * @method getProfileOgTitle
 * @param  {object} funeralHome   the funeral home object
 * @return {string} returns the fully open graph title
 */
export function getProfileOgTitle(funeralHome) {
  return `${funeralHome.name} - Planning, Obituaries, Services | Legacy.com`;
}

/**
 * Constructs the og:title for facebook
 * @method getPersonOgTitle
 * @param  {object} name        name object
 * @return {string} returns the fully formed canonical url
 */
export function getPersonOgTitle(name) {
  const {first, last} = name || {};
  const formattedName = _.trim(_.compact([first, last]).join(' '));

  if (formattedName.length === 0) {
    return 'View Obituary and express your condolences';
  }
  return `View ${formattedName}'s Obituary and express your condolences`;
}

/**
 * Constructs the canonical url eliminating unwanted query parameters
 * @method getCanonicalUrl
 * @param  {string}        vanity        vanity url
 * @param  {string}        originalUri   request uri
 * @return {string}        returns the fully formed canonical url
 */
export function getCanonicalUrl(vanity, originalUri) {
  const original = originalUri
    .replace(/[?&]page=[0-9]+/, '')
    .replace(/[?&]testWebcast=[^&]+/, '')
    .replace(/[?&]view=[a-zA-z]+/, '')
    .replace(/funeral-homes\/planning.*/, 'funeral-homes/planning/home');

  return `${vanity}${original}`;
}

/**
 * Constructs the canonical url for fh profile page.
 * If required data is missing, returns null
 * @method buildDirectoryPath
 * @param  {object}        funeralHome   funeral home object
 * @return {string}        returns the fully formed canonical url
 */
export function buildDirectoryPath(funeralHome) {
  const {funeralHomeId, name = '', location = {}} = funeralHome || {};
  const {city = {}, state = {}} = location;
  const dashedFhName = formatNameForUrl(name);

  if (state.urlName && city.urlName && dashedFhName && funeralHomeId) {
    return `/funeral-homes/${state.urlName}/${
      city.urlName
    }/${dashedFhName}/fh-${funeralHomeId}`;
  }

  return null;
}

/**
 * constructs basic page meta tags
 * @method buildMetaTagObject
 * @param  {string}        title   title
 * @param  {string}        ogTitle   ogTitle
 * @param  {string}        description   description
 * @param  {string}        imageUrl   imageUrl
 * @param  {string}        seoUrl   seoUrl
 * @param  {string}        canonicalUrl   canonicalUrl
 * @param  {string}        latestDate   latestDate
 * @param  {string}        publishDate   publishDate
 * @return {string}        returns the fully formed canonical url
 */
export function buildMetaTagObject(
  title,
  ogTitle,
  description,
  imageUrl,
  seoUrl,
  canonicalUrl,
  latestDate,
  publishDate
) {
  const metaTagObject = {
    title,
    description,
    image_src: imageUrl,
    canonical: seoUrl,
    generator: 'ReactJS',
    'og:type': 'article',
    'og:url': canonicalUrl,
    'og:title': ogTitle,
    'og:description': description,
    'og:image': imageUrl,
    'og:site_name': 'Legacy.com',
    'og:updated_time': latestDate,
    'twitter:card': 'summary',
    'twitter:url': canonicalUrl,
    'twitter:site': '@LegacyObits',
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image': imageUrl,
    'dcterms.creator': 'admin',
    'dcterms.language': 'en',
    'dcterms.format': 'text/html',
    'dcterms.type': 'Text',
    'dcterms.title': title,
    'dcterms.description': description,
    'dcterms.identifier': canonicalUrl,
    'dcterms.date': publishDate,
    'article:modified_time': latestDate,
    'article:published_time': publishDate,
  };

  metaTagObject.pageSchemaTags = {
    title,
    description,
    image: imageUrl,
  };

  return metaTagObject;
}

/**
 * builds schema tags for SEO
 * @param  {Object} tags - tags
 * @return {String} Compiled template strings.
 */
function buildPageSchemaTags(tags) {
  return _.map(tags, (value, name) => {
    let content = value;

    content = sanitizeText(content);
    const template = _.template(
      '<meta itemprop="<%= name %>" content="<%= content %>">'
    );

    return template({name, content});
  }).join('\n');
}

/**
 * Semmantic wrapper for opengraph check
 * @param  {String}  key Meta tag name.
 * @return {Boolean} True if key starts with 'og:'.
 */
function isOpenGraph(key) {
  return _.startsWith(key, 'og:');
}

/**
 * Build a JSON-LD tag
 * @param  {Object} dataObject JSON Object to input.
 * @return {String} A JSON-LD tag.
 */
function buildJsonLdTag(dataObject) {
  return `<script type="application/ld+json">${JSON.stringify(
    dataObject
  )}</script>`;
}

/**
 * format meta object
 * @param  {Object} person person
 * @param  {String} canonicalUrl canonical url (usually current url)
 * @return {object} meta object
 */
function formatObitMetaObject(person, canonicalUrl) {
  const obitId = _.head(_.get(person, 'obitSources.person'));
  const obit = _.find(_.get(person, 'obituaries', []), ['sourceId', obitId]);
  let obitText = _.get(person, 'displayText.text', '');
  const imageUri = _.get(person, 'portraitImage.href', null);

  if (obitText) {
    const MAX_META_DESC_CHARS = 165;

    obitText = _.truncate(sanitizeText(obitText), {
      length: MAX_META_DESC_CHARS,
      separator: ' ',
    });
  }
  const seoUrl = _.get(person, 'meta.seoCanonicalUrl') || canonicalUrl;
  const title = formatTitle(
    _.get(person, 'name.first'),
    _.get(person, 'name.last'),
    _.get(person, 'location.city'),
    _.get(person, 'location.state')
  );
  const ogTitle = getPersonOgTitle(_.get(person, 'name'));
  const description = _.get(person, 'meta.description') || obitText;
  const imageUrl = imageUri
    ? `${buildUrl(
        conf.environmentalUrl.cacheUrl,
        imageUri
      )}x?w=200&h=200&option=3&fc=fff`
    : `${
        conf.vanityDomain
      }/funeral-homes/obituaries/name/assets/share_candle.jpg`;
  const publishDate = _.get(obit, 'dateCreated', '');
  const latestDate = _.get(person, 'meta.latestDate', publishDate);

  const metaTagObject = buildMetaTagObject(
    title,
    ogTitle,
    description,
    imageUrl,
    seoUrl,
    canonicalUrl,
    latestDate,
    publishDate
  );

  if (!_.isEmpty(person)) {
    const mainObitSitename = _.get(person, 'obituaries[0].gaSitename', '')
    const personPageType = _.get(person, 'personLayout.personPageType');
    const isAggregatedOrFHObit = _.includes(['batesville', GA_SITENAMES.tributes, GA_SITENAMES.dignityMemorial], mainObitSitename.toLowerCase());
    const isAggregatedOrFHPageType = _.includes([PERSON_PAGE_TYPE.batesvillePersonPage, PERSON_PAGE_TYPE.forestLawnPersonPage, PERSON_PAGE_TYPE.tributesFhPersonPage], personPageType);
    const obitDaysActive = _.get(person, 'customDimensions.obitDaysActive', null);
  
    if (obitDaysActive < 30 && (isAggregatedOrFHObit || isAggregatedOrFHPageType)) {
      metaTagObject.newsArticle = addNewsArticleSchema(person);
    }
    metaTagObject.person = addPersonSchema(person);

    const eventsSchema = addPersonEventsSchema(person);
    const paidObitsItemListSchema = addItemListSchema(person);

    if (eventsSchema.length > 0) {
      metaTagObject.personEvents = eventsSchema;
    }
    if (!_.isEmpty(paidObitsItemListSchema)) {
      metaTagObject.paidObitsItemList = paidObitsItemListSchema;
    }
  }

  return metaTagObject;
}

/**
 * format meta object
 * @param  {Object} funeralHome funeralHome
 * @param  {Object} upcomingServices fh services
 * @param  {String} canonicalUrl canonical url (usually current url)
 * @return {object} meta object
 */
function formatProfileMetaObject(funeralHome, upcomingServices, canonicalUrl) {
  const title = `${funeralHome.name} - ${
    funeralHome.location.city.fullName
  } - ${funeralHome.location.state.code} | Legacy.com`;
  const ogTitle = getProfileOgTitle(funeralHome);
  const description =
    _.get(funeralHome, 'meta.description') ||
    getProfileDescription(funeralHome);
  const imageUrl = funeralHome.logoImageUrl;
  const seoUrl =
    buildUrl(conf.vanityDomain, buildDirectoryPath(funeralHome)) ||
    canonicalUrl;
  const latestDate = funeralHome.modifiedTime;
  const publishDate = funeralHome.funeralHomeDateStarted;

  const metaTagObject = buildMetaTagObject(
    title,
    ogTitle,
    description,
    imageUrl,
    seoUrl,
    canonicalUrl,
    latestDate,
    publishDate
  );

  if (!_.isEmpty(funeralHome)) {
    const funeralHomeAndEventSchema = addFuneralHomeAndEventSchema(
      funeralHome,
      upcomingServices
    );

    metaTagObject.funeralHome = funeralHomeAndEventSchema.funeralHome;
    metaTagObject.funeralHomeEvents = funeralHomeAndEventSchema.events;
  }
  return metaTagObject;
}

/**
 * format meta object
 * @param  {String} planningType planningType
 * @param  {String} canonicalUrl canonical url (usually current url)
 * @return {object} meta object
 */
function formatPlanningMetaObject(planningType, canonicalUrl) {
  const isGeneral = Boolean(planningType === 'general');
  const title = `Funeral Planning ${
    isGeneral
      ? 'Guide | Legacy.com'
      : '- Make Funeral Arrangements at Legacy.com'
  }`;
  const description = isGeneral
    ? 'Get Answers About Planning Ahead. Download your free funeral planning guide & checklist'
    : 'Compare funeral planning providers and read our expert advice, free guides, forms and advanced planning tools that are convenient and easy to use.';
  const imageUrl = `${
    conf.vanityDomain
  }/funeral-homes/obituaries/name/assets/planning.png`;
  const date = '2018-06-27T12:00:00.000Z';

  const metaTagObject = buildMetaTagObject(
    title,
    title,
    description,
    imageUrl,
    canonicalUrl,
    canonicalUrl,
    date,
    date
  );

  return metaTagObject;
}

/**
 * format meta object
 * @param  {Object} search search
 * @return {object} meta object
 */
function formatSearchMetaObject(search) {
  const title = `${
    search.meta.location.city
  } Funeral Homes - Funeral Homes in ${search.meta.location.city} | Legacy.com`;
  // TODO for radius search, should this be title = `${search.meta.location.city} Funeral Homes - Funeral Homes in ${search.meta.location.city}, ${search.meta.location.state} for ${search.meta.location.postalCode} | Legacy.com`;
  // TODO description should come from service as something like (verify with Brian Free and .NET code)
  // `${search.meta.location.city} funeral homes on Legacy.com. Read recent obituaries, find service information, &amp; send flowers. Powered by Legacy.com`;
  // `${search.meta.location.city}, ${search.meta.location.state} funeral homes on Legacy.com. Read recent obituaries, find service information, &amp; send flowers. Powered by Legacy.com`;
  const description = _.get(search, 'meta.description', '');

  const metaTagObject = {
    title,
    description,
    robots: 'noindex,nofollow',
  };

  return metaTagObject;
}

/**
 * Updates metadata params to inclunde injected tockens if found
 * @param  {string} requestType page type requested
 * @param  {string} robotsMeta  index follow info
 * @param  {object} person      person
 * @param  {object} funeralHome  funeral home
 * @param  {object} upcomingServices  upcoming fh services
 * @param  {object} search    search
 * @param  {string} originalUrl req.originalUrl
 * @return {object}             meta object
 */
function getMetaDataObject(
  requestType,
  robotsMeta,
  person,
  funeralHome,
  upcomingServices,
  search,
  // NOTE: this origionalUrl doens't include the host name
  originalUrl
) {
  const canonicalUrl = getCanonicalUrl(conf.vanityDomain, originalUrl);
  let metaObject = {
    title: '',
    description: '',
    robots: 'noindex, nofollow',
  };

  if (requestType === 'obit') {
    metaObject = formatObitMetaObject(person, canonicalUrl);
    metaObject.robots = robotsMeta;
  } else if (requestType === 'profile') {
    metaObject = formatProfileMetaObject(
      funeralHome,
      upcomingServices,
      originalUrl
    );
    metaObject.robots = robotsMeta;
  } else if (requestType === 'planning') {
    metaObject = formatPlanningMetaObject(null, canonicalUrl);
    metaObject.robots = robotsMeta;
  } else if (requestType === 'generalPlanning') {
    metaObject = formatPlanningMetaObject('general', canonicalUrl);
    metaObject.robots = robotsMeta;
  } else if (requestType === 'search') {
    metaObject = formatSearchMetaObject(search);
  }

  return metaObject;
}

/**
 * Takes metaData object and pushes rendered tags to metaTags array
 * @param  {string} requestType page type requested
 * @param  {string} robotsMeta  index follow info
 * @param  {object} person      person
 * @param  {object} funeralHome  funeral home
 * @param  {object} upcomingServices  upcoming fh events
 * @param  {object} search  search
 * @param  {string} reqOrigionalUrl req.originalUrl
 * @return {Array} Contains rendered meta tags
 */
export function buildMetaTags(
  requestType,
  robotsMeta,
  person,
  funeralHome,
  upcomingServices,
  search,
  reqOrigionalUrl
) {
  const metaData = getMetaDataObject(
    requestType,
    robotsMeta,
    person,
    funeralHome,
    upcomingServices,
    search,
    reqOrigionalUrl
  );
  const metaTags = [];

  _.forEach(metaData, (value, key) => {
    if (key === 'pageSchemaTags') {
      metaTags.push(buildPageSchemaTags(value));
    } else if (key === 'newsArticle') {
      metaTags.push(
        `<script data-schema="NewsArticle" type="application/ld+json">${JSON.stringify(
          value
        )}</script>`
      );
    } else if (
      _.includes(['person', 'paidObitsItemList', 'funeralHome'], key) &&
      !_.isEmpty(value)
    ) {
      metaTags.push(buildJsonLdTag(value));
    } else if (_.includes(['personEvents', 'funeralHomeEvents'], key)) {
      value.map(index => {
        metaTags.push(buildJsonLdTag(index));
      });
    } else if (key === 'title') {
      metaTags.push(`<title>${value}</title>`);
    } else if (_.isString(value)) {
      if (
        _.includes(['image_src', 'canonical'], key) &&
        !isOpenGraph(key)
        // validUrl.isWebUri(value), we check it's a good url, but since we sometimes pass down only the path we can't
      ) {
        metaTags.push(`<link rel="${key}" href="${value}">`);
      } else if (isOpenGraph(key)) {
        metaTags.push(`<meta property="${key}" content="${value}">`);
      } else {
        metaTags.push(`<meta name="${key}" content="${value}">`);
      }
    }
  });

  return metaTags.join('\n');
}

/**
 * @param  {Object}        person       person Object
 * @param  {Object}        guestBook    guestBook Object
 * @param  {number|string} fhco         primary funeralHome affiliate
 * @param  {number}        fhid         primary funeral home id
 * @param  {number}        pid          person id
 * @param  {string}        requestView  requested view
 * @param  {string}        personPageType type of person page
 * @return {Object}        data layer object
 */
export function getDataLayer(
  person,
  guestBook,
  fhco,
  fhid,
  pid,
  requestView,
  personPageType
) {
  const dataLayer = {};
  const isFuneralHomePage = isFuneralHomePageType(personPageType);

  /* Custom Dimensions */
  if (person.customDimensions) {
    // obit days active - custom dimension #12
    dataLayer.obitDaysActive = person.customDimensions.obitDaysActive;

    // page category - custom dimension #14
    const columnView =
      person.customDimensions.obitDaysActive > 6 ? 'gb_view' : 'obit_view';

    dataLayer.analyticPageCategory = getAnalyticPageCategory(
      personPageType,
      columnView
    );

    // first to file - custom dimension #24
    dataLayer.firstToFile = person.customDimensions.firstToFile;

    // days since zero - custom dimension #25
    if (person.customDimensions.daysSinceZero === null) {
      dataLayer.daysSinceZero = 'null';
    } else {
      dataLayer.daysSinceZero = person.customDimensions.daysSinceZero;
    }
  } else {
    // page category - fall back for custom dimension #14
    dataLayer.analyticPageCategory = getAnalyticPageCategory(
      personPageType,
      'obit_view'
    );
  }

  // pid
  dataLayer.pid = pid;

  // page name - custom dimension #19
  dataLayer.pageName = 'person';

  // product name - custom dimension #20
  dataLayer.productName = 'obit-fhd';

  // person page view - custom dimension #28
  if (isFuneralHomePage) {
    dataLayer.personPageView = requestView;
    dataLayer.fhId = fhid;
  }

  // funeral home id - custom dimension #27
  dataLayer.FHCo = fhco;

  // guest book ID if guestbook is active - custom dimension #23
  if (guestBook.allowGuestBook) {
    dataLayer.gbid = guestBook.sourceId;
  }

  return dataLayer;
}

/**
 * Gets the global advertising variables
 * @param   {object}  adConfig   global ad configuration data
 * @param   {object}  personData a subset of the person data
 * @param   {string}  referrer Referrer header value
 * @returns {object}  global variable object
 */
export function getGlobalAdVars(adConfig, personData, referrer) {
  const {affiliates, flowers, guestBook, obituaries, personId, name} =
    personData || {};
  const {first, last} = name || {};
  const fhid = _.get(flowers, 'funeralHome.sourceId');
  const ref = getRefValue(referrer);

  const fhAffiliates = _.reduce(
    obituaries,
    (acc, obit) => {
      if (obit.funeralHomeId === fhid) {
        const fhAffiliate = _.find(affiliates, ['sourceId', obit.affiliateId]);

        if (_.get(fhAffiliate, 'affiliateType') === 'Funeral Home Publisher') {
          acc.push(fhAffiliate.sourceId);
        }
      }
      return acc;
    },
    []
  );

  return _.merge(
    {},
    {
      pid: personId,
      gbentries: _.get(guestBook, 'totalCondolencesCount') || '0',
      fn: first,
      ln: last,
      FHCo: _.head(fhAffiliates) || '0',
      fhid: fhid || '0',
      ref,
    },
    ...adConfig
  );
}

/**
 * Gets the global advertising variables
 * @param   {object}  fhProfileData the gateway fh profile data
 * @param   {object}  affiliateData the gateway affiliate data
 * @param   {string}  referer the referrer header string
 * @returns {object}  global variable object
 *
 * TODO need to define the values for
 * searchstate and ref
 * e.g., globalAdVars.searchstate = ?;
 * e.g., globalAdVars.ref = ?; defaults to 'Legacy?'
 */
export function getProfileGlobalAdVars(fhProfileData, affiliateData, referer) {
  const {funeralHomeId, location = {}, profileData = {}} = fhProfileData || {};
  const {city = {}, state = {}, postalCode} = location;
  const {fullName: cityName} = city;
  const {code: stateCode} = state;

  const {location: affiliateLocation = {}, siteName, corporateId} =
    affiliateData || {};
  const {state: affState = {}, country: affCountry = {}} = affiliateLocation;
  const {code: affStateName} = affState;
  const {code: affCountryCode} = affCountry;
  const ref = getRefValue(referer);

  return _.merge(
    {},
    {
      category: 'funeral-home-detail',
      cobrand: siteName || 'Legacy',
      corp: corporateId || '0',
      affstate: affStateName || '0',
      country: affCountryCode || '0',
      fhid: funeralHomeId || '0',
      fhcity: cityName,
      fhstate: stateCode,
      fhzip: postalCode,
      FHCo: profileData.fhco,
      v: 2,
      ref,
    }
  );
}



// WEBPACK FOOTER //
// ./src/appModules/utilities/metaDataUtilities.js